import React, { useState } from 'react';
import { X, Hash, Lock, Users, CheckCircle, AlertCircle, UserPlus } from 'lucide-react';
import API_URL from '../config/ApiUrl';
import { useTheme } from '../arrangement/ThemeContext';
const InviteCodeModal = ({ isOpen, onClose, currentUser, onJoinSuccess }) => {
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [foundRoom, setFoundRoom] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const { colors } = useTheme();
  
  const handleClose = () => {
    setInviteCode('');
    setError('');
    setFoundRoom(null);
    setConfirming(false);
    onClose();
  };

  const searchByInviteCode = async () => {
    if (inviteCode.length !== 8) {
      setError('Invite code must be 8 characters long');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/rooms/join-by-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          invite_code: inviteCode.toUpperCase(),
          action: 'search'
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setFoundRoom(data.room);
        setConfirming(true);
      } else {
        setError(data.error || 'Invalid invite code');
        setFoundRoom(null);
      }
    } catch (err) {
      setError('Failed to verify invite code');
      setFoundRoom(null);
    } finally {
      setLoading(false);
    }
  };

  const confirmJoinRoom = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/rooms/join-by-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          invite_code: inviteCode.toUpperCase(),
          action: 'join'
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        onJoinSuccess({
          success: true,
          message: data.message,
          room: data.room,
          role: data.role
        });
        handleClose();
      } else {
        setError(data.error || 'Failed to join room');
      }
    } catch (err) {
      setError('Failed to join room');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length <= 8) {
      setInviteCode(value);
      setError('');
      setFoundRoom(null);
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '16px',
        padding: '24px',
        maxWidth: '500px',
        width: '100%',
        border: '1px solid #333',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
        color: 'white'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: '#e5e5e5',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Hash size={20} style={{ color: '#8b5cf6' }} />
            {confirming ? 'Confirm Room' : 'Enter Invite Code'}
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {!confirming ? (
          <>
            {/* Info */}
            <div style={{
              background: 'rgba(139, 92, 246, 0.1)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Lock size={16} style={{ color: '#8b5cf6' }} />
              <span style={{ fontSize: '14px', color: '#d1d5db' }}>
                Enter an 8-character invite code to join a private sesh room
              </span>
            </div>

            {/* Invite Code Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                color: '#888',
                marginBottom: '8px'
              }}>
                Invite Code
              </label>
              <input
                type="text"
                value={inviteCode}
                onChange={handleCodeChange}
                placeholder="C1B1D89E"
                style={{
                  width: '100%',
                  padding: '16px',
                  background: colors.bgCard,
                  border: '1px solid #333',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '18px',
                  textAlign: 'center',
                  letterSpacing: '2px',
                  fontFamily: 'monospace',
                  fontWeight: '600',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                maxLength={8}
              />
              <div style={{
                fontSize: '12px',
                color: '#666',
                marginTop: '4px',
                textAlign: 'center'
              }}>
                {inviteCode.length}/8 characters
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#ef4444'
              }}>
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {/* Search Button */}
            <button
              onClick={searchByInviteCode}
              disabled={inviteCode.length !== 8 || loading}
              style={{
                width: '100%',
                padding: '12px',
                background: (inviteCode.length === 8 && !loading) ? '#8b5cf6' : '#333',
                border: 'none',
                borderRadius: '8px',
                cursor: (inviteCode.length === 8 && !loading) ? 'pointer' : 'not-allowed',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: (inviteCode.length === 8 && !loading) ? 1 : 0.7
              }}
            >
              {loading ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #666',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Searching...
                </>
              ) : (
                <>
                  <Hash size={16} />
                  Find Room
                </>
              )}
            </button>
          </>
        ) : (
          <>
            {/* Room Confirmation */}
            <div style={{
              background: colors.bgCard,
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <h3 style={{
                margin: '0 0 8px 0',
                fontSize: '18px',
                fontWeight: '600',
                color: '#e5e5e5',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Lock size={16} style={{ color: '#8b5cf6' }} />
                {foundRoom.title}
              </h3>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <img
                  src={foundRoom.host?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${foundRoom.host?.username}`}
                  alt={foundRoom.host?.username}
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%'
                  }}
                />
                <span style={{ fontSize: '14px', color: '#888' }}>
                  Hosted by {foundRoom.host?.username}
                </span>
              </div>

              {foundRoom.description && (
                <p style={{
                  margin: '8px 0',
                  fontSize: '14px',
                  color: '#ccc',
                  lineHeight: '1.4'
                }}>
                  {foundRoom.description}
                </p>
              )}

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginTop: '12px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  color: '#888'
                }}>
                  <Users size={12} style={{ color: '#8b5cf6' }} />
                  {foundRoom.participant_count || 0}/{foundRoom.max_participants} members
                </div>
                
                <div style={{
                  padding: '2px 6px',
                  background: foundRoom.is_live ? '#00d4aa' : '#666',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: '600',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px'
                }}>
                  <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: 'white',
                    animation: foundRoom.is_live ? 'pulse 2s infinite' : 'none'
                  }} />
                  {foundRoom.is_live ? 'LIVE' : 'OFFLINE'}
                </div>
              </div>
            </div>

            {/* Confirmation Message */}
            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#10b981'
            }}>
              <CheckCircle size={16} />
              <span style={{ fontSize: '14px' }}>
                Is this the room you want to join?
              </span>
            </div>

            {/* Error Message */}
            {error && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#ef4444'
              }}>
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => {
                  setConfirming(false);
                  setFoundRoom(null);
                  setError('');
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'transparent',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: '#888',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Back
              </button>
              
              <button
                onClick={confirmJoinRoom}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: loading ? '#333' : '#8b5cf6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #666',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Joining...
                  </>
                ) : (
                  <>
                    <UserPlus size={16} />
                    Join Room
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default InviteCodeModal;